import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LinkSimple, Plugs, PlugsConnected, CheckCircle, Buildings, ArrowClockwise, Trash, PaperPlaneRight } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const CARD = { background: "#fff", border: "1px solid var(--omh-border)", borderRadius: 12, padding: "20px 22px" };
const LBL = { fontSize: 10.5, fontWeight: 700, color: "var(--omh-text-muted)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, display: "block" };
const INP = { width: "100%", height: 36, boxSizing: "border-box", padding: "6px 10px", border: "1px solid var(--omh-border)", borderRadius: 8, fontSize: 13, background: "#fff", color: "var(--omh-text)" };
const BTN = { display: "inline-flex", alignItems: "center", gap: 8, height: 38, padding: "0 16px", borderRadius: 9, fontSize: 13.5, fontWeight: 700, border: "none", cursor: "pointer" };

const emptyLine = () => ({ item_id: "", quantity: "1", unit_price: "", description: "" });

export default function QuickBooks() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);     // {connected, env, realm_id, company_name, ...}
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [items, setItems] = useState([]);
  const [refsLoading, setRefsLoading] = useState(false);

  const [vendorId, setVendorId] = useState("");
  const [apAccountId, setApAccountId] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [memo, setMemo] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [pushing, setPushing] = useState(false);
  const [lastPO, setLastPO] = useState(null);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/qbo/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRefs = useCallback(async () => {
    setRefsLoading(true);
    try {
      const [v, a, i] = await Promise.all([
        api.get("/qbo/refs/vendors"),
        api.get("/qbo/refs/accounts"),
        api.get("/qbo/refs/items"),
      ]);
      setVendors(v.data || []);
      setAccounts(a.data || []);
      setItems(i.data || []);
      if ((a.data || []).length === 1) setApAccountId(a.data[0].id);
    } catch (e) {
      toast.error("Could not load QuickBooks lists", { description: e?.response?.data?.detail?.slice?.(0, 180) });
    } finally {
      setRefsLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Handle the OAuth redirect flag (?qbo=connected|error)
  useEffect(() => {
    const flag = params.get("qbo");
    if (!flag) return;
    if (flag === "connected") toast.success("QuickBooks connected");
    else toast.error("QuickBooks connection failed", { description: params.get("msg") || "" });
    params.delete("qbo"); params.delete("msg");
    setParams(params, { replace: true });
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (status?.connected) loadRefs(); }, [status?.connected, loadRefs]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/qbo/connect");
      window.location.assign(data.authorization_url);
    } catch (e) {
      toast.error("Could not start QuickBooks connect", { description: e?.response?.data?.detail });
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks? You'll need to reconnect to push purchase orders.")) return;
    try {
      await api.post("/qbo/disconnect");
      toast.success("QuickBooks disconnected");
      setVendors([]); setAccounts([]); setItems([]);
      loadStatus();
    } catch (e) {
      toast.error("Disconnect failed", { description: e?.response?.data?.detail });
    }
  };

  const setLine = (idx, key, val) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, [key]: val } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (idx) => setLines((ls) => (ls.length === 1 ? ls : ls.filter((_, i) => i !== idx)));

  const canPush = vendorId && apAccountId && lines.every((l) => l.item_id && +l.quantity > 0 && +l.unit_price > 0);

  const pushPO = async () => {
    if (!canPush) { toast.error("Fill vendor, A/P account and every line (item, qty, unit price)"); return; }
    setPushing(true);
    setLastPO(null);
    try {
      const payload = {
        vendor_id: vendorId,
        ap_account_id: apAccountId,
        doc_number: docNumber || undefined,
        memo: memo || undefined,
        lines: lines.map((l) => ({
          item_id: l.item_id,
          quantity: +l.quantity,
          unit_price: +l.unit_price,
          amount: +(+l.quantity * +l.unit_price).toFixed(2),
          description: l.description || undefined,
        })),
      };
      const { data } = await api.post("/qbo/purchase-orders", payload);
      setLastPO(data);
      toast.success(`Purchase Order created in QuickBooks`, { description: `Doc #${data.doc_number || data.id} · Total ${data.total}` });
      setLines([emptyLine()]); setDocNumber(""); setMemo("");
    } catch (e) {
      toast.error("Push to QuickBooks failed", { description: (e?.response?.data?.detail || "").toString().slice(0, 300) });
    } finally {
      setPushing(false);
    }
  };

  const connected = !!status?.connected;

  return (
    <Layout breadcrumb={["Orders", "QuickBooks Purchase Orders"]}>
      <div style={{ maxWidth: 960, display: "flex", flexDirection: "column", gap: 20 }} data-testid="quickbooks-page">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 26, fontWeight: 800, margin: 0 }}>
            <LinkSimple size={26} weight="duotone" /> QuickBooks Purchase Orders
          </h1>
          <p style={{ color: "var(--omh-text-muted)", fontSize: 13.5, marginTop: 6 }}>
            Connect your Intuit QuickBooks Online company and push Purchase Orders directly from the OMS.
          </p>
        </div>

        {/* Connection status */}
        <div style={{ ...CARD, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }} data-testid="qbo-status-card">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 46, height: 46, borderRadius: 11, display: "grid", placeItems: "center",
              background: connected ? "rgba(34,160,88,.12)" : "rgba(120,120,120,.1)" }}>
              {connected ? <PlugsConnected size={26} weight="duotone" color="#22a058" /> : <Plugs size={26} weight="duotone" color="#888" />}
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 15 }} data-testid="qbo-conn-state">
                {loading ? "Checking…" : connected ? "Connected" : "Not connected"}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--omh-text-muted)" }}>
                {connected
                  ? <span><Buildings size={13} weight="duotone" style={{ verticalAlign: -2 }} /> {status.company_name || `Realm ${status.realm_id}`} · <b style={{ textTransform: "uppercase" }}>{status.env}</b></span>
                  : <>Environment: <b style={{ textTransform: "uppercase" }}>{status?.env || "sandbox"}</b></>}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {!connected ? (
              <button data-testid="qbo-connect-btn" onClick={connect} disabled={connecting || loading}
                style={{ ...BTN, background: "#2ca01c", color: "#fff", opacity: connecting ? 0.7 : 1 }}>
                <PlugsConnected size={17} weight="bold" /> {connecting ? "Redirecting…" : "Connect QuickBooks"}
              </button>
            ) : (
              <>
                <button data-testid="qbo-refresh-btn" onClick={loadStatus} style={{ ...BTN, background: "var(--omh-surface,#f1f3f5)", color: "var(--omh-text)" }}>
                  <ArrowClockwise size={16} weight="bold" /> Refresh
                </button>
                <button data-testid="qbo-disconnect-btn" onClick={disconnect} style={{ ...BTN, background: "#fff", color: "#d33", border: "1px solid #f1c2c2" }}>
                  <Plugs size={16} weight="bold" /> Disconnect
                </button>
              </>
            )}
          </div>
        </div>

        {/* PO form */}
        {connected && (
          <div style={{ ...CARD }} data-testid="qbo-po-form">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>Create Purchase Order</h2>
              {refsLoading && <span style={{ fontSize: 12, color: "var(--omh-text-muted)" }}>Loading vendors & items…</span>}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <div>
                <label style={LBL}>Vendor</label>
                <select data-testid="qbo-vendor-select" style={INP} value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                  <option value="">Select vendor…</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>Accounts Payable account</label>
                <select data-testid="qbo-account-select" style={INP} value={apAccountId} onChange={(e) => setApAccountId(e.target.value)}>
                  <option value="">Select A/P account…</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label style={LBL}>Document # (optional)</label>
                <input data-testid="qbo-docnumber-input" style={INP} value={docNumber} onChange={(e) => setDocNumber(e.target.value)} placeholder="e.g. PO134871" />
              </div>
              <div>
                <label style={LBL}>Memo (optional)</label>
                <input data-testid="qbo-memo-input" style={INP} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Internal note" />
              </div>
            </div>

            <label style={LBL}>Line items</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {lines.map((l, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 90px 120px 2fr 36px", gap: 8, alignItems: "center" }} data-testid={`qbo-line-${i}`}>
                  <select style={INP} value={l.item_id} data-testid={`qbo-line-item-${i}`}
                    onChange={(e) => { const it = items.find((x) => x.id === e.target.value); setLine(i, "item_id", e.target.value); if (it?.unit_price != null && !l.unit_price) setLine(i, "unit_price", String(it.unit_price)); }}>
                    <option value="">Select item…</option>
                    {items.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                  </select>
                  <input style={INP} type="number" min="0" step="any" placeholder="Qty" value={l.quantity} data-testid={`qbo-line-qty-${i}`} onChange={(e) => setLine(i, "quantity", e.target.value)} />
                  <input style={INP} type="number" min="0" step="any" placeholder="Unit price" value={l.unit_price} data-testid={`qbo-line-price-${i}`} onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                  <input style={INP} placeholder="Description (optional)" value={l.description} data-testid={`qbo-line-desc-${i}`} onChange={(e) => setLine(i, "description", e.target.value)} />
                  <button type="button" title="Remove line" onClick={() => removeLine(i)} data-testid={`qbo-line-remove-${i}`}
                    style={{ ...BTN, padding: 0, width: 36, height: 36, justifyContent: "center", background: "#fff", color: "#d33", border: "1px solid var(--omh-border)" }}>
                    <Trash size={15} weight="bold" />
                  </button>
                </div>
              ))}
            </div>

            <button type="button" onClick={addLine} data-testid="qbo-add-line-btn"
              style={{ ...BTN, background: "transparent", color: "var(--omh-primary,#1769aa)", padding: "6px 0", marginTop: 8 }}>
              + Add line
            </button>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18, borderTop: "1px solid var(--omh-border)", paddingTop: 16 }}>
              <div style={{ fontSize: 13.5 }}>
                Total: <b data-testid="qbo-po-total">{lines.reduce((s, l) => s + (+l.quantity || 0) * (+l.unit_price || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
              </div>
              <button data-testid="qbo-push-btn" onClick={pushPO} disabled={!canPush || pushing}
                style={{ ...BTN, background: "#2ca01c", color: "#fff", opacity: (!canPush || pushing) ? 0.5 : 1 }}>
                <PaperPlaneRight size={17} weight="bold" /> {pushing ? "Pushing…" : "Push to QuickBooks"}
              </button>
            </div>

            {lastPO && (
              <div style={{ marginTop: 16, background: "rgba(34,160,88,.08)", border: "1px solid rgba(34,160,88,.25)", borderRadius: 9, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }} data-testid="qbo-po-result">
                <CheckCircle size={20} weight="fill" color="#22a058" />
                <span style={{ fontSize: 13.5 }}>Created in QuickBooks — <b>Doc #{lastPO.doc_number || lastPO.id}</b> (ID {lastPO.id}), total {lastPO.total}.</span>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
